import * as React from 'react';
import groupBy from "lodash-es/groupBy";
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { CoughSession } from 'webportalclient';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { Link } from 'react-router-dom';
import { DonutChartData, DonutChart } from '../../Graphs/DonutChart';
import { CoughSessionStatusEnum } from '../../misc/api/apiTypes';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { Authorization } from '../../misc/components/Authorization';
import { useSecurityContext } from '../../Core/SecurityContext';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';

declare const rootUrl: string;

interface SeriesSummaryTableProps {
    series: Array<DonutChartData>;
}

const SeriesSummaryTable = (props: SeriesSummaryTableProps) => {
    return (
        <table className='table'>
            <thead>
                <tr>
                    <th></th>
                    <th>Status</th>
                    <th>Sessions</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {props.series.map((item, index) => <SeriesSummaryRow key={index} value={item.value} name={item.label} color={item.color} id={item.id} />)}
            </tbody>
        </table>
    );
}

interface SeriesSummaryRowProps {
    color: string;
    name: string;
    value: number;
    id: string;
}

const SeriesSummaryRow = (props: SeriesSummaryRowProps) => {
    if (props.value < 1) return null;

    return (
        <tr>
            <td><i className='fas fa-square' aria-hidden='true' style={{ color: props.color }}></i></td>
            <td>{props.name}</td>
            <td>{props.value}</td>
            <td className='text-right'><Link to={`/cough?statusId=${props.id}`} className='btn btn-primary btn-xs'>View</Link></td>
        </tr>
    );
}



const CoughSessionBreakdownWidgetContent = () => {
    const client = useWebPortalApiClient();
    const [coughSessions, setCoughSessions] = React.useState<CoughSession[]>();
    const { coughSessionStatuses } = useReferenceContextProvider();
    const { study } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const getData = async () => {
            const select = "status";
            const filter = `visit/subject/site/studyId eq ${study.id}`;
            const coughSessionResult = await client.client.getCoughSessions(select, filter, undefined, undefined);
            const groupedByStatus = groupBy(coughSessionResult.value, s => s.status);
            const totalPerGroup = Object.entries(groupedByStatus).map(g => ({ status: g[0], total: (g[1] as CoughSession[]).length }));

            setCoughSessions(totalPerGroup);
        }

        getData();

        return () => {
            client.abortController.abort();
        }
    }, []);

    if (!coughSessions)
        return <LoadingWithMessage message="Loading Cough Session Breakdown..." />

    const getColor = (id: string) => {
        switch (id) {
            case CoughSessionStatusEnum.AwaitingData:
                return "#2874A6";
            case CoughSessionStatusEnum.ReceivingData:
                return "#5DADE2";
            case CoughSessionStatusEnum.AwaitingProcessing:
                return "#7D3C98";
            case CoughSessionStatusEnum.Processing:
                return "#F1C40F";
            case CoughSessionStatusEnum.AwaitingQualityCheck:
                return "#17A589";
            case CoughSessionStatusEnum.AwaitingAnalysis:
                return "#D934DB";
            case CoughSessionStatusEnum.AnalysisInProgress:
                return "#FFEB3B";
            case CoughSessionStatusEnum.AnalysisComplete:
                return "#6DCC4F";
            case CoughSessionStatusEnum.DeviceUnassigned:
                return "#E59400";
            case CoughSessionStatusEnum.QualityCheckRejected:
                return "#CB4335";
            case CoughSessionStatusEnum.ReceivingDataMailed:
                return "#5B3B00";
            case CoughSessionStatusEnum.ProcessingFailed:
                return "#777777";
            default:
                return "#777777"
        }
    }

    const pieData = coughSessions.map((a: any) => ({
        id: a.status,
        label: coughSessionStatuses.find(o => o.id === a.status)?.name,
        value: a.total,
        color: getColor(a.status)
    }));

    return (
        <div className='row'>
            <div className='col-md-12' style={{ "height": "400px" }}>
                <DonutChart data={pieData} />
            </div>
            <div className='col-md-12'>
                <SeriesSummaryTable series={pieData} />
            </div>
        </div>               
    );
}

export const CoughSessionBreakdownWidget = () => {
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();

    return (
        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.ViewCoughSessionBreakdownWidget}>
            <ErrorBoundary>
                <div className="col-md-6">
                    <div id="CoughSessionBreakdownWidget" className="ibox">
                        <div className="ibox-title">
                            <h5 className="text-primary">Cough Session Breakdown</h5>
                        </div>
                        <div className="ibox-content">
                            <CoughSessionBreakdownWidgetContent />
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Authorization>
    );
}
